import { ParseResult, Schema } from "effect";

type EncodedValue = Record<string, unknown>;

const segmentsToBracketKey = (segments: (string | number)[]): string => {
  const [head, ...rest] = segments.map(String);
  if (!head) return "";
  return head + rest.map((segment) => `[${segment}]`).join("");
};

const isSimpleSegment = (s: string): boolean =>
  s.length > 0 && !/[.\[\]]/.test(s);

const segmentsToDotKey = (segments: (string | number)[]): string => {
  if (segments.length === 0) return "";

  let result = "";

  segments.forEach((seg, index) => {
    if (typeof seg === "number") {
      // numeric segments always as brackets
      result += `[${seg}]`;
      return;
    }

    const str = String(seg);

    if (index === 0) {
      // first segment
      if (isSimpleSegment(str)) {
        result = str;
      } else {
        result = `[${str}]`;
      }
    } else {
      if (isSimpleSegment(str)) {
        result += `.${str}`;
      } else {
        result += `[${str}]`;
      }
    }
  });

  return result;
};

const AUTO = Symbol("auto");

type PathSegment = string | number | typeof AUTO;

const parseBracketKey = (key: string): PathSegment[] => {
  const segments: PathSegment[] = [];

  // 1. Split on "." only when we are NOT inside brackets
  const parts: string[] = [];
  let current = "";
  let depth = 0;

  for (let i = 0; i < key.length; i++) {
    const ch = key[i];

    if (ch === "[") {
      depth++;
      current += ch;
    } else if (ch === "]") {
      depth = Math.max(0, depth - 1);
      current += ch;
    } else if (ch === "." && depth === 0) {
      // dot as a path separator (outside brackets)
      if (current.length > 0) {
        parts.push(current);
        current = "";
      }
    } else {
      current += ch;
    }
  }

  if (current.length > 0) {
    parts.push(current);
  }

  // 2. For each part (no dots outside brackets), run bracket parser
  for (const part of parts) {
    const re = /([^[\]]+)|\[(.*?)\]/g; // NEW: fresh regex for each part
    let match: RegExpExecArray | null;

    while ((match = re.exec(part)) !== null) {
      if (match[1] !== undefined) {
        // bare segment before any brackets within this part
        segments.push(match[1]);
      } else {
        const content = match[2] ?? "";
        if (content === "") {
          // "[]"
          segments.push(AUTO);
        } else if (/^\d+$/.test(content)) {
          // "[0]" -> 0
          segments.push(Number(content));
        } else {
          // "[properties]" -> "properties"
          segments.push(content);
        }
      }
    }
  }

  return segments;
};

type ObjectPath = (string | number)[];

const getPath = (obj: unknown, segments: ObjectPath): unknown => {
  let cur: any = obj;
  for (const seg of segments) {
    if (cur == null) return undefined;
    if (typeof seg === "number") {
      if (!Array.isArray(cur)) return undefined;
      cur = cur[seg];
    } else {
      cur = cur[seg];
    }
  }
  return cur;
};

const setNestedValue = (
  root: any,
  segments: PathSegment[],
  value: unknown,
): void => {
  let node: any = root;

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const isLast = i === segments.length - 1;
    const next = segments[i + 1];

    if (typeof seg === "string") {
      if (isLast) {
        // Leaf: aggregate repeated values
        const existing = node[seg];
        if (existing === undefined) {
          node[seg] = value;
        } else if (Array.isArray(existing)) {
          existing.push(value);
        } else {
          node[seg] = [existing, value];
        }
        return;
      }

      // Non-last: ensure container
      const shouldBeArray = typeof next === "number" || next === AUTO;

      if (!(seg in node) || node[seg] == null) {
        node[seg] = shouldBeArray ? [] : {};
      }

      node = node[seg];
    } else if (typeof seg === "number") {
      // Node should be an array here
      if (!Array.isArray(node)) {
        // If this ever happens, your form shape is inconsistent,
        // but we can reset to a new array to avoid crashing.
        node = [];
      }

      if (isLast) {
        const existing = node[seg];
        if (existing === undefined) {
          node[seg] = value;
        } else if (Array.isArray(existing)) {
          existing.push(value);
        } else {
          node[seg] = [existing, value];
        }
        return;
      }

      const shouldBeArray = typeof next === "number" || next === AUTO;

      if (node[seg] === undefined || node[seg] == null) {
        node[seg] = shouldBeArray ? [] : {};
      }

      node = node[seg];
    } else if (seg === AUTO) {
      // node must already be an array (created by previous string segment)
      const arr: any[] = node;

      const remaining = segments
        .slice(i + 1)
        .filter((s): s is string | number => s !== AUTO);

      // Simple "items[]" (degenerate) case: no remaining real path
      if (isLast && remaining.length === 0) {
        arr.push(value);
        return;
      }

      // Non-degenerate: items[][properties][key]
      let candidateIndex = -1;
      for (let j = 0; j < arr.length; j++) {
        const candidate = arr[j];
        if (getPath(candidate, remaining) === undefined) {
          candidateIndex = j;
          break;
        }
      }

      if (candidateIndex === -1) {
        candidateIndex = arr.length;
        arr.push({});
      }

      // Here, there must be remaining segments, so we don't hit isLast
      node = arr[candidateIndex];
    }
  }
};

type KeyValueEntries = Iterable<[string, any]>;

/**
 * Turn things like:
 *   items[0][properties][key] = "foo"
 *   items[][properties][key] = "bar"
 * into nested JS objects/arrays.
 */
const entriesToNestedObject = (entries: KeyValueEntries): any => {
  const root: any = {};

  for (const [rawKey, rawValue] of entries) {
    const segments = parseBracketKey(rawKey);
    // URLSearchParams is always string values, so this is simple:
    const value = rawValue;
    setNestedValue(root, segments, value);
  }

  return root;
};

export const formDataToNestedObject = (formData: FormData): any =>
  entriesToNestedObject(formData.entries());

export const searchParamsToNestedObject = (params: URLSearchParams): any =>
  entriesToNestedObject(params.entries());

export const objectToFormData = (encoded: EncodedValue): FormData => {
  const fd = new FormData();

  const append = (value: unknown, path: (string | number)[]) => {
    if (value == null) return;

    // Treat arrays separately if you want indices in the path
    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        append(item, [...path, index]);
      });
      return;
    }

    if (value instanceof File || value instanceof Blob) {
      const key = segmentsToBracketKey(path);
      fd.append(key, value);
      return;
    }

    if (typeof value === "object") {
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        append(v, [...path, k]);
      }
      return;
    }

    // Primitive (string/number/boolean/etc.)
    const key = segmentsToBracketKey(path);
    fd.append(key, String(value));
  };

  for (const [k, v] of Object.entries(encoded)) {
    append(v, [k]);
  }

  return fd;
};

export const objectToSearchParams = (
  encoded: EncodedValue,
): URLSearchParams => {
  const params = new URLSearchParams();

  const append = (value: unknown, path: (string | number)[]) => {
    if (value == null) return;

    if (Array.isArray(value)) {
      // Decide: repeated key vs indexed keys
      const isPrimitiveArray = value.every(
        (item) => item == null || typeof item !== "object",
      );

      if (isPrimitiveArray) {
        // e.g. filter.p.product_type=A&filter.p.product_type=B
        value.forEach((item) => {
          if (item == null) return;
          const key = segmentsToDotKey(path);
          const v = String(item);
          if (v === "") return;
          params.append(key, v);
        });
      } else {
        // Arrays of objects / mixed arrays still get indices
        value.forEach((item, index) => {
          append(item, [...path, index]);
        });
      }
      return;
    }

    if (typeof value === "object") {
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        append(v, [...path, k]);
      }
      return;
    }

    // Primitive: string/number/boolean/etc.
    const key = segmentsToDotKey(path);
    const v = String(value);
    if (v === "") return;
    params.append(key, v);
  };

  for (const [k, v] of Object.entries(encoded)) {
    append(v, [k]);
  }

  return params;
};

export const parseSearchParams = <A, I, R>(schema: Schema.Schema<A, I, R>) =>
  Schema.compose(
    Schema.transformOrFail(Schema.instanceOf(URLSearchParams), Schema.Any, {
      strict: true,
      decode: (i, _, ast) =>
        ParseResult.try({
          try: () => searchParamsToNestedObject(i),
          catch: (e) =>
            new ParseResult.Type(
              ast,
              i,
              e instanceof Error ? e.message : String(e),
            ),
        }),
      encode: (a, _, ast) =>
        ParseResult.try({
          try: () => objectToSearchParams(a),
          catch: (e) =>
            new ParseResult.Type(
              ast,
              a,
              e instanceof Error ? e.message : String(e),
            ),
        }),
    }),
    schema,
  );
